import { FC, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import EditIcon from '@material-ui/icons/Edit';
import { ContentState, EditorState, convertToRaw } from 'draft-js';
import toast from 'react-hot-toast';
import htmlToDraft from 'html-to-draftjs';
import draftToHtml from 'draftjs-to-html';
import { ButtonBase, makeStyles } from '@material-ui/core';

import { editCorps, getCorps } from 'api/corps';
import { UploadMedia } from 'components/Shared/UploadMedia';
import Loader from 'components/UI/Loader';
import { useAppDispatch, useAppSelector } from 'store';
import { mediaActions, trimMedia } from 'store/reducers/mediaSlice';
import { CorpType } from 'store/types/newsTypes';
import { getConfig } from 'config/config';
import { IonIcon } from 'components/UI/IonIcon';
import PostGallery from 'components/Shared/PostGallery';
import { useLanguage } from 'languages/languageContext';
import { fileType } from 'store/reducers/attachments';
import { UserListItem } from 'components/UI/UserListItem';
import { imagePath } from 'utilities';
import { clearKreiseSubscribers } from 'store/reducers/kreise';
import { Corps } from 'scenes/ProfileCorps/model';

const { theme } = getConfig();

export const truncateArrayWithCount = (array: string[], maxLength: number = 8) => {
  if (array.length > maxLength) {
    const truncated = array.slice(0, maxLength).join(', ');
    const remainingCount = array.length - maxLength;
    return truncated + ` (+${remainingCount})`;
  }
  return array.join(', ');
};

export const getChargeFromCorp = (corps: Corps[], id: string): string => {
  if (!corps?.length) return '';
  return corps.reduce((acc, item) => {
    if (item?.corpsname === id && Array.isArray(item.charge)) {
      acc = truncateArrayWithCount(item?.charge);
    }
    return acc;
  }, '');
};

export const CorpsScreen: FC<{ groupId: string }> = ({ groupId }) => {
  const styles = useStyles({});
  const history = useHistory();

  const params = useParams<{ id: string }>();
  const corpId = groupId || params?.id;

  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(true);
  const [corps, setCorps] = useState<CorpType>();
  const [editMode, setEditMode] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const { images } = useAppSelector((state) => state.media);

  const user = useAppSelector((state) => state.users.user);
  const isAdmin = !!corps?.admins?.find((el) => el.user?._id === user?._id);

  const { messages, corpsScreen } = useLanguage();

  useEffect(() => {
    fetchCorps();
    return () => {
      dispatch(mediaActions.deleteAllAttachments());
    };
  }, []);

  useEffect(() => {
    if (corps?.corpsname) {
      if (corps.description) {
        const contentBlock = htmlToDraft(corps.description);
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        const editorState = EditorState.createWithContent(contentState);
        setEditorState(editorState);
      }
      dispatch(
        mediaActions.setEditingAttachments({
          // @ts-ignore
          images: corps.photo.map((el: string | fileType) =>
            typeof el === 'object'
              ? el
              : {
                  url: el,
                  id: el,
                },
          ),
          documents: [],
          videos: [],
        }),
      );
    }
  }, [corps?.corpsname]);

  useEffect(() => {
    if (editMode && images?.every((el) => el.url)) uploadImages();
  }, [images.every((el) => el.url), images.length]);

  const fetchCorps = async () => {
    setLoading(true);
    const res = await getCorps(corpId);

    setCorps(res);
    setLoading(false);
  };

  const toEditMode = () => {
    if (editMode) {
      uploadImages();
      setEditMode(false);
      return;
    }
    setEditMode(true);
  };

  const saveDescription = async () => {
    setLoading(true);
    // Save the description via the API
    const { description } = await editCorps(groupId, { description: getMarkup() });
    // Update the local state with the new description
    setCorps({ ...corps, description });
    setLoading(false);
    toast.success(messages.changesSaved);
    // Turn off edit mode after saving the description
    setEditMode(false);
  };

  const uploadImages = async () => {
    const { images: trimmed } = trimMedia({ images });
    const { photo } = await editCorps(groupId, { photo: trimmed });
    setCorps({ ...corps, photo });
    toast.success(messages.changesSaved);
  };

  const onEditorStateChange = (editorState) => setEditorState(editorState);

  const getMarkup = () => {
    const rawContentState = convertToRaw(editorState.getCurrentContent());
    const markup = draftToHtml(rawContentState);
    return markup;
  };

  const EditBtn = () =>
    (isAdmin || corps?.allowMembersChangePhoto || corps?.allowMembersEditDescription) && (
      <ButtonBase
        onClick={toEditMode}
        style={{
          color: theme.ACTIVE_INPUT,
          height: 30,
          width: 30,
          borderRadius: 30,
        }}
      >
        {!editMode ? (
          <div
            style={{
              color: theme.BACKGROUND_PRIMARY,
              backgroundColor: theme.ACTIVE_INPUT,
              width: 30,
              height: 30,
              borderRadius: 30,
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <EditIcon color="inherit" />
          </div>
        ) : (
          <IonIcon
            name={'checkmark'}
            size={20}
            color={theme.BACKGROUND}
            style={{
              backgroundColor: theme.ACTIVE_INPUT,
              borderRadius: 40,
              padding: 5,
            }}
          />
        )}
      </ButtonBase>
    );

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px' }}>
        <div className={styles.header} style={{ fontSize: 22 }}>
          {corps?.corpsname}
        </div>
        <EditBtn />
      </div>
      <div style={{ overflow: 'hidden' }}>
        {loading ? (
          <Loader />
        ) : (
          <>
            {editMode && corps?.allowMembersChangePhoto ? (
              <div style={{ paddingRight: 20, paddingLeft: 20 }}>
                <UploadMedia
                  viewType="singleImageInput"
                  lables={{ addLabel: corpsScreen.addPicture, editLabel: corpsScreen.editPicture }}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <PostGallery images={corps?.photo.map((el) => (typeof el === 'object' ? el : { url: el }))} />
              </div>
            )}
            <div style={{ padding: 15 }}>
              <div style={{ marginBottom: 15 }}>
                {(editMode || corps.description) && <span className={styles.header}>{corpsScreen.info}</span>}
                {editMode && corps?.allowMembersEditDescription ? (
                  <div
                    style={{
                      border: 'solid',
                      borderWidth: 1,
                      borderRadius: 8,
                      overflow: 'hidden',
                      borderColor: theme.BACKGROUND_SECONDARY,
                    }}
                  >
                    <Editor
                      editorState={editorState}
                      toolbarClassName="toolbarClassName border-0 border-b-2"
                      wrapperClassName="wrapperClassName border-0"
                      editorClassName="editorClassName px-3"
                      onEditorStateChange={onEditorStateChange}
                      onBlur={saveDescription}
                    />
                  </div>
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: corps.description }} />
                )}
              </div>
              {!!corps.admins?.length && (
                <>
                  <span className={styles.header}>{corpsScreen.admins}</span>
                  {corps.admins.map(({ user }, index) => (
                    <UserListItem
                      key={index}
                      name={`${user?.firsname || user?.name} ${user?.lastname}`}
                      image={imagePath(null, user?.photo)}
                      description={user?.wohnort}
                      corpName={getChargeFromCorp(user?.corp, corps?._id)}
                      onClick={() => {
                        history.push(`/profile-details/${user?._id}`, { userId: user?._id });
                        dispatch(clearKreiseSubscribers());
                      }}
                    />
                  ))}
                </>
              )}

              {corps?.website && (
                <div style={{ marginBottom: 15, display: 'flex', flexDirection: 'column' }}>
                  <span className={styles.header}>{corpsScreen.website} </span>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={corps.website}
                    style={{ textDecoration: 'none', fontSize: 18, fontWeight: 'bolder' }}
                  >
                    {corps.website}
                  </a>
                </div>
              )}
              {corps?.email && (
                <div style={{ marginBottom: 15, display: 'flex', flexDirection: 'column' }}>
                  <span className={styles.header}>{corpsScreen.email} </span>
                  <a
                    href={`mailto:${corps.email}`}
                    style={{ textDecoration: 'none', fontSize: 18, fontWeight: 'bolder' }}
                  >
                    {corps.email}
                  </a>
                </div>
              )}
              <span className={styles.header}>{corpsScreen.members}</span>
            </div>
          </>
        )}
      </div>
    </>
  );
};

const useStyles = makeStyles({
  header: {
    fontSize: 20,
    color: theme.TEXT_PRIMARY,
    fontWeight: 'bolder',
    padding: '10px 0',
    alignSelf: 'flex-start',
  },
});
